package com.arena_web.ui.components.widgets

import androidx.compose.runtime.Composable
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.s
import com.arena_web.ui.utils.sBorderRadius
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.styleModifier

/**
 * Box with a gradient border that respects rounded corners.
 *
 * The gradient is painted on an absolutely-positioned child layer and clipped to
 * the border ring via the `mask-composite: exclude` trick — this works for any
 * `borderRadius` (unlike `border-image`, which ignores the radius).
 *
 * Use anywhere a pill, chip, or surface needs a thin gradient outline that follows
 * its rounded shape (e.g. action buttons, segmented pills, icon buttons, cards).
 *
 * @param borderRadius corner radius in design units (passed through `s()`).
 * @param background optional CSS background string for the fill (gradient or solid).
 * @param borderGradient CSS gradient applied to the border ring.
 * @param borderWidth border thickness as a CSS length, e.g. "0.5px" or "1px".
 */
@Composable
fun GradientBorderBox(
    modifier: Modifier = Modifier,
    borderRadius: Number = 100,
    background: String? = null,
    borderGradient: String = AppGradients.ButtonBorderGradientPrimary,
    borderWidth: String = "0.5px",
    contentAlignment: Alignment = Alignment.Center,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = modifier
            .sBorderRadius(borderRadius)
            .styleModifier {
                if (background != null) property("background", background)
                property("position", "relative")
                property("box-sizing", "border-box")
            },
        contentAlignment = contentAlignment,
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("border", "$borderWidth solid transparent")
                    property("border-radius", s(borderRadius))
                    property("background", "$borderGradient border-box")
                    property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                    property("-webkit-mask-composite", "xor")
                    property("mask-composite", "exclude")
                    property("pointer-events", "none")
                },
        )
        content()
    }
}
